import React, { useMemo, useState } from "react";
import { healthColorForScore, TILE_TINT } from "@/lib/pillarTone";

const CHIP = {
  green: { cls: "text-[#166534] bg-[#dcfce7] border-[#86efac]", icon: "✓" },
  yellow: { cls: "text-[#854d0e] bg-[#fef9c3] border-[#fde047]", icon: "!" },
  red: { cls: "text-[#991b1b] bg-[#fee2e2] border-[#fca5a5]", icon: "▲" },
  grey: { cls: "text-[#4b5563] bg-[#f3f4f6] border-[#e5e7eb]", icon: "–" },
};
const BAR = { green: "bg-[#16a34a]", yellow: "bg-[#b45309]", red: "bg-[#dc2626]", grey: "bg-[#9ca3af]" };
const r1 = (v) => Math.round(v * 10) / 10;

export const BandChip = ({ score, completion, testid }) => {
  const b = healthColorForScore(score, completion);
  const c = CHIP[b.tone];
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11.5px] font-semibold ${c.cls}`} data-testid={testid}>
      <span aria-hidden>{c.icon}</span>{b.label}
    </span>
  );
};

const enrich = (pillars) => (pillars || []).map((p, order) => {
  const band = healthColorForScore(p.score, p.completion_pct);
  const hasPrev = p.prev_score != null && p.score != null;
  const prevBand = hasPrev ? healthColorForScore(p.prev_score, 100) : null;
  return { ...p, order, band, delta: hasPrev ? r1(p.score - p.prev_score) : null,
    crossed: hasPrev && prevBand.tone !== band.tone ? band.label : null };
});

const SORTS = {
  lowest: (a, b) => (a.score ?? 999) - (b.score ?? 999) || a.order - b.order,
  order: (a, b) => a.order - b.order,
  change: (a, b) => Math.abs(b.delta ?? -1) - Math.abs(a.delta ?? -1) || a.order - b.order,
};

export const heatSummary = (rows) => {
  const n = (t) => rows.filter((r) => r.score != null && r.band.tone === t).length;
  const up = rows.filter((r) => r.delta > 0).length;
  const down = rows.filter((r) => r.delta < 0).length;
  const base = `${n("green")} Healthy · ${n("yellow")} Caution · ${n("red")} High Risk`;
  return rows.some((r) => r.delta != null) ? `${base} · ${up} improved, ${down} declined since last period` : base;
};

const Delta = ({ p, tid }) => p.delta == null ? null : (
  <div className="mt-2 text-[13px] text-[#374151]" data-testid={tid}>
    <span className={`font-bold ${p.delta > 0 ? "text-[#15803d]" : p.delta < 0 ? "text-[#b91c1c]" : "text-[#4b5563]"}`}>
      <span aria-hidden>{p.delta > 0 ? "▲ " : p.delta < 0 ? "▼ " : ""}</span>{p.delta > 0 ? "+" : ""}{p.delta}
    </span>{" "}from {p.prev_score}
  </div>
);

const MiniBar = ({ p }) => (
  <div className="mt-2" aria-hidden>
    <div className="relative h-2 rounded-full bg-[#e5e7eb]">
      <div className={`h-full rounded-full ${BAR[p.band.tone]}`} style={{ width: `${Math.max(0, Math.min(100, p.score ?? 0))}%` }} />
      <span className="absolute -top-0.5 h-3 w-px bg-[#6b7280]" style={{ left: "60%" }} />
      <span className="absolute -top-0.5 h-3 w-px bg-[#6b7280]" style={{ left: "80%" }} />
    </div>
    <div className="relative h-3 text-[10px] text-[#4b5563]">
      <span className="absolute -translate-x-1/2" style={{ left: "60%" }}>60</span>
      <span className="absolute -translate-x-1/2" style={{ left: "80%" }}>80</span>
    </div>
  </div>
);

const Card = ({ p, viewer, ids, onOpen, onAddEvidence, extra }) => {
  const full = (p.completion_pct ?? 0) >= 100;
  const label = `${p.title}: ${p.score != null ? `${p.score} out of 100, ${p.band.label}` : "not started"}${p.delta != null ? `, ${p.delta > 0 ? "up" : p.delta < 0 ? "down" : "no change"} ${Math.abs(p.delta)} from ${p.prev_score}` : ""}`;
  return (
    <div className={`flex flex-col overflow-hidden rounded-xl border ${TILE_TINT[p.band.tone]}`} data-testid={ids.card(p)} data-tone={p.band.tone}>
      <div className={`h-1.5 ${BAR[p.band.tone]}`} data-testid={ids.strip?.(p)} />
      <button type="button" onClick={() => onOpen?.(p)} disabled={!onOpen} aria-label={label} data-testid={ids.open?.(p)}
        className="flex flex-1 flex-col justify-start rounded-lg p-4 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#007a9e] enabled:hover:bg-black/[0.02]">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-[15px] font-bold leading-snug text-[#111827]">{p.title}</h3>
          <BandChip score={p.score} completion={p.completion_pct} testid={ids.chip?.(p)} />
        </div>
        <div className="mt-2 text-[#111827]"><span className="text-3xl font-bold tracking-tight">{p.score ?? "—"}</span><span className="text-xs text-[#4b5563]"> /100</span></div>
        {p.score != null && <MiniBar p={p} />}
        <Delta p={p} tid={ids.prev?.(p)} />
        {p.crossed && (
          <span className={`mt-1.5 inline-block rounded px-2 py-0.5 text-[11.5px] font-semibold ${CHIP[p.band.tone].cls}`} data-testid={`${ids.card(p)}-crossed`}>Crossed into {p.crossed}</span>
        )}
        {full ? (
          <div className="mt-2 text-[12.5px] text-[#374151]" data-testid={`${ids.card(p)}-complete`}>✓ All {p.questions_total} answered</div>
        ) : (
          <div className="mt-2" data-testid={`${ids.card(p)}-progress`}>
            <div className="h-1.5 rounded-full bg-[#e5e7eb]"><div className="h-full rounded-full bg-[#475569]" style={{ width: `${p.completion_pct ?? 0}%` }} /></div>
            <div className="mt-1 text-[12.5px] text-[#374151]">{p.questions_answered} of {p.questions_total} answered</div>
          </div>
        )}
      </button>
      {(p.evidence_gaps > 0 || extra) && (
        <div className="mx-4 mb-3 border-t border-black/5 pt-2 text-[12.5px] text-[#374151]">
          {p.evidence_gaps > 0 && (
            <div data-testid={`${ids.card(p)}-evidence`}>
              {p.evidence_gaps} evidence item{p.evidence_gaps === 1 ? "" : "s"} missing
              {viewer === "school" && onAddEvidence && (
                <> · <button type="button" onClick={() => onAddEvidence(p)} className="font-semibold text-[#005f7b] underline" data-testid={`${ids.card(p)}-add-evidence`}>Add evidence</button></>
              )}
            </div>
          )}
          {extra?.(p)}
        </div>
      )}
    </div>
  );
};

const Cell = ({ p, ids, onOpen }) => (
  <button type="button" onClick={() => onOpen?.(p)} disabled={!onOpen} data-testid={`${ids.card(p)}-cell`}
    className={`rounded-lg border-2 p-3 text-left ${TILE_TINT[p.band.tone]} focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#007a9e]`}>
    <div className="text-[13px] font-semibold leading-snug text-[#111827]">{p.title}</div>
    <div className="mt-1 flex flex-wrap items-center gap-1.5">
      <span className="text-xl font-bold text-[#111827]">{p.score ?? "—"}</span>
      <BandChip score={p.score} completion={p.completion_pct} />
      {p.delta != null && <span className={`text-[12px] font-bold ${p.delta > 0 ? "text-[#15803d]" : p.delta < 0 ? "text-[#b91c1c]" : "text-[#4b5563]"}`}>{p.delta > 0 ? "▲ +" : p.delta < 0 ? "▼ " : ""}{p.delta}</span>}
    </div>
  </button>
);

export const LowestPillarCallout = ({ pillars, testid = "lowest-pillar-callout" }) => {
  const low = enrich(pillars).filter((p) => p.score != null).sort(SORTS.lowest)[0];
  if (!low) return null;
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-xl border border-[#bfe3ee] bg-[#e6f4f8] px-4 py-3 text-[14px] text-[#0f2530]" data-testid={testid}>
      <span className="font-bold">Lowest pillar:</span> {low.title} ({low.score})
      <BandChip score={low.score} completion={low.completion_pct} />
    </div>
  );
};

// Pillar Health Heat Map v2 — shared by School and Portfolio views.
export const PillarHeatMapV2 = ({ pillars, viewer = "school", ids, gridTestId, onOpen, onAddEvidence, extra, showCallout = true }) => {
  const [sort, setSort] = useState("lowest");
  const [mode, setMode] = useState("cards");
  const rows = useMemo(() => enrich(pillars), [pillars]);
  const sorted = useMemo(() => [...rows].sort(SORTS[sort]), [rows, sort]);
  const seg = (on) => `min-h-[40px] px-3 text-[13px] font-semibold ${on ? "bg-[#111827] text-white" : "bg-white text-[#111827] hover:bg-[#f3f4f6]"}`;
  return (
    <div className="space-y-3" data-testid="pillar-heatmap-v2">
      {showCallout && <LowestPillarCallout pillars={pillars} />}
      <div className="rounded-2xl border border-[#e5e7eb] bg-white p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-[#111827]">Pillar Health Heat Map</h2>
            <p className="mt-0.5 text-[14px] font-medium text-[#374151]" data-testid="heatmap-summary">{heatSummary(rows)}</p>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[12px] text-[#374151]" data-testid="heatmap-legend">
              <span className="inline-flex items-center gap-1"><BandChip score={90} completion={100} /> 80–100</span>
              <span className="inline-flex items-center gap-1"><BandChip score={70} completion={100} /> 60–79</span>
              <span className="inline-flex items-center gap-1"><BandChip score={10} completion={100} /> 0–59</span>
              <span>▲▼ change since last period</span>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-[13px] text-[#374151]" htmlFor="heatmap-sort">Sort</label>
            <select id="heatmap-sort" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="heatmap-sort"
              className="min-h-[40px] rounded-md border border-[#d1d5db] bg-white px-2 text-[13px]">
              <option value="lowest">Lowest score first</option>
              <option value="order">Pillar order</option>
              <option value="change">Biggest change</option>
            </select>
            <div className="inline-flex overflow-hidden rounded-md border border-[#d1d5db]" role="group" aria-label="View">
              <button type="button" aria-pressed={mode === "cards"} className={seg(mode === "cards")} onClick={() => setMode("cards")} data-testid="heatmap-mode-cards">Cards</button>
              <button type="button" aria-pressed={mode === "grid"} className={seg(mode === "grid")} onClick={() => setMode("grid")} data-testid="heatmap-mode-grid">Heat map</button>
            </div>
          </div>
        </div>
        {mode === "cards" ? (
          <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-4" data-testid={gridTestId}>
            {sorted.map((p) => <Card key={p.id} p={p} viewer={viewer} ids={ids} onOpen={onOpen} onAddEvidence={onAddEvidence} extra={extra} />)}
          </div>
        ) : (
          <div className="mt-4 grid grid-cols-2 gap-2 md:grid-cols-4" data-testid={`${gridTestId}-cells`}>
            {sorted.map((p) => <Cell key={p.id} p={p} ids={ids} onOpen={onOpen} />)}
          </div>
        )}
      </div>
    </div>
  );
};

export default PillarHeatMapV2;
